/**
 * The browser half of the OS file service (apps/server/src/platform/files). Modules upload
 * through their own routes, which decide who may attach a file to what; every read comes back
 * through the OS route, `/api/files/:id`, which checks the file's permission. These helpers
 * keep that contract in one place for every module.
 */

import { ApiError, messageOf, statusMessage } from './api-client';

/** Mirrors the server's MAX_UPLOAD_BYTES (platform/files/service.ts) for a pre-check. */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/** Where a stored file is read from. `download` forces a download over an inline view. */
export function fileUrl(id: string, options: { download?: boolean } = {}): string {
  return `/api/files/${encodeURIComponent(id)}${options.download ? '?download=1' : ''}`;
}

/** "812 B", "64 KB", "3.4 MB" — as the prototypes print sizes. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** "PDF", "XLSX" — the extension badge, four letters at most; "FILE" when there is none. */
export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return (dot > 0 ? name.slice(dot + 1, dot + 5) : '').toUpperCase() || 'FILE';
}

export type PreviewKind = 'image' | 'video' | 'audio' | 'frame' | 'none';

/** How the preview overlay can show a type; matches what the OS route serves inline. */
export function previewKind(contentType: string): PreviewKind {
  if (/^image\/(png|jpe?g|gif|webp|avif)$/.test(contentType)) return 'image';
  if (contentType.startsWith('video/')) return 'video';
  if (contentType.startsWith('audio/')) return 'audio';
  if (/^(application\/pdf|text\/plain|text\/csv)$/.test(contentType)) return 'frame';
  return 'none';
}

/**
 * Files over the cap, by name — checked before upload so nothing big is sent for nothing. Pass
 * `maxBytes` when the module has its own cap (the server's `ModuleFileSpec`).
 */
export function oversized(files: readonly File[], maxBytes = MAX_UPLOAD_BYTES): string[] {
  return files.filter((file) => file.size > maxBytes).map((file) => file.name);
}

/**
 * POSTs one file as multipart (`file`, plus any `fields`) to a module's upload route, with the
 * session cookie. Resolves to the parsed JSON body; rejects with the server's error message.
 */
export async function uploadFile<T>(
  url: string,
  file: File,
  fields: Record<string, string> = {},
): Promise<T> {
  const body = new FormData();
  body.append('file', file, file.name);
  for (const [key, value] of Object.entries(fields)) body.append(key, value);
  const response = await fetch(url, { method: 'POST', body, credentials: 'include' });
  const text = await response.text();
  if (!response.ok)
    throw new ApiError(
      response.status,
      messageOf(text, statusMessage(response.status, `Upload failed (${response.status}).`)),
    );
  return (text ? JSON.parse(text) : null) as T;
}
